import type { EmbedConfig } from "@/lib/schemas/checkout/embed-config";
import type { Link } from "@/lib/schemas/paycode/link";
import { getEnabledPaymentMethods } from "@/lib/schemas/paycode/payment-methods";
import { buildSteps } from "@/lib/store/checkout";
import { AddressFormSkeleton } from "./address-form";
import { CustomerFormSkeleton } from "./customer-form";
import { DataPaneSkeleton } from "./data-pane";
import { InstructionsSkeleton } from "./instructions/instruction-container";
import { PaymentFormSkeleton } from "./payment-form";
import { PaymentMethodPickerSkeleton } from "./payment-method-picker";
import { StepperSkeleton } from "./stepper";

type CheckoutSkeletonProps = {
  link?: Link | null;
  embedded?: boolean;
  embedConfig?: EmbedConfig | null;
};

export const CheckoutSkeleton = ({
  link,
  embedded,
  embedConfig,
}: CheckoutSkeletonProps) => {
  const steps = link ? buildSteps(link) : null;
  const firstStep = steps?.[0] ?? "payment";
  const method = link?.initialPaymentMethods ?? "card";
  const methodCount = link
    ? getEnabledPaymentMethods(link.paymentMethods).length
    : 2;

  const renderFirstStep = () => {
    if (firstStep === "customer")
      return (
        <CustomerFormSkeleton dynamicFields={link?.dynamicFields.length ?? 0} />
      );
    if (firstStep === "shipping" || firstStep === "billing") {
      const savedAddresses =
        firstStep === "shipping"
          ? link?.shippingAddressOptions
          : link?.billingAddressOptions;
      return <AddressFormSkeleton savedAddresses={!!savedAddresses?.length} />;
    }

    return (
      <div className="space-y-8">
        <PaymentMethodPickerSkeleton count={methodCount} />
        <div className="w-full pb-4">
          {method === "card" ? (
            <PaymentFormSkeleton
              msi={link ? Object.values(link.msi ?? {}).some(Boolean) : true}
              savedCards={link ? !!link.client?.tokens?.length : true}
              tokenizable={!!link?.tokenizable}
            />
          ) : (
            <InstructionsSkeleton method={method} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-dvh w-dvw flex-col space-y-8 lg:h-dvh lg:flex-row lg:space-y-0">
      <DataPaneSkeleton
        link={link}
        embedded={embedded}
        embedConfig={embedConfig}
      />
      <div className="flex w-full flex-col items-center overflow-x-hidden bg-surface-static px-4 md:px-0 lg:h-full lg:basis-1/2 lg:items-start">
        <div className="h-full w-full max-w-md space-y-8 md:max-w-lg lg:pt-16 lg:pl-8 2xl:max-w-2xl">
          <StepperSkeleton count={steps?.length ?? 3} />
          {renderFirstStep()}
        </div>
      </div>
    </div>
  );
};
